import React from "react";
import { ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import { DateTime } from "luxon";

import DelayInput from "../DelayInput";
import ViewToolbar from "../ViewToolbar";

import { V2 } from "../../Api";
import { formatDateLocalDayOfWeek, formatTimeLocal } from "../../Utilities";
import { IAuthProps } from "../AuthContext";
import HasRight from "../HasRight";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../../table.css";
import "../viewer.css";

interface IAppointmentListViewState {
    appointments: any[];

    locations: any[];
    searchText: string;
    searchLocation: string;
    searchOffset: number;
    // searchPerson: string;
    searchStatus: string;
    searchVisitType: string;

    isLoading: boolean | string;

    [name: string]: any;
}

const PAGE_SIZE = 50;

class SurgeryListView extends React.Component<RouteComponentPropsHistory<any, any> & IAuthProps, IAppointmentListViewState> {
    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IAuthProps>) {
        super(props);

        this.state = {
            appointments: [],

            locations: [],
            searchLocation: props.location.state ? props.location.state.searchLocation : "",
            searchOffset: props.location.state && props.location.state.searchOffset ? props.location.state.searchOffset : 0,
            searchStatus: props.location.state ? props.location.state.searchStatus : "",
            searchText: props.location.state ? props.location.state.searchText : "",
            searchVisitType: props.location.state ? props.location.state.searchVisitType : "",

            isLoading: true
        };

        for (const appt of this.state.appointments) {
            appt.appointmentTimestamp = new Date(appt.appointmentTimestamp);
            appt.timestamp = new Date(appt.timestamp);
        }

        this.handleElementChange = this.handleElementChange.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.previousPage = this.previousPage.bind(this);
    }

    public async componentDidMount() {
        try {
            const locations = await V2.get<any[]>("locations?type=surgery");

            this.setState({
                locations
            }, () => this.search());
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public render() {
        let table;

        if (this.state.isLoading === true) {
            table = <div className="spinner-center lds-dual-ring" />;
        }
        else if (typeof this.state.isLoading === "string") {
            table = <span className="error-text">{this.state.isLoading}</span>;
        }
        else {
            let tbody;

            if (this.state.appointments.length === 0) {
                tbody = (
                    <tr>
                        <td colSpan={10}>No appointments found</td>
                    </tr>
                );
            }
            else {
                tbody = this.state.appointments.map(a => (
                    <tr key={a.id}>
                        <td data-caption="First Name: ">{a.patient.name.first}</td>
                        <td data-caption="Last Name: ">{a.patient.name.last}</td>
                        {/*<td data-caption="Type: ">{a.appointmentType}</td>*/}
                        <td data-caption="Date: " className="wrap">{formatDateLocalDayOfWeek(a.date)}</td>
                        <td data-caption="Time: ">{a.time ? formatTimeLocal(DateTime.fromISO(`${a.date.substring(0, 10)}T${a.time}`, { zone: "America/New_York" })) : "-"}</td>
                        <td data-caption="Location: " className="wrap">{a.location.shortName || a.location.name}</td>
                        {/*<td data-caption="Created On: ">{formatDateTimeLocalNumeric(a.timestamp)}</td>*/}
                        <td data-caption="Status: ">{this.statusToText(a.status)}</td>
                        <td>
                            <Link className="button" to={`/surgery/${a.id}`}>View</Link>
                        </td>
                    </tr>
                ));
            }

            table = (
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th>First Name</th>
                            <th>Last Name</th>
                            {/*<th>Type</th>*/}
                            <th>Date</th>
                            <th className="nowrap">Time</th>
                            <th>Location</th>
                            {/*<th>Created On</th>*/}
                            <th>Status</th>
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Search results</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tbody}
                    </tbody>
                </table>
            );
        }

        return (
            <div className="viewer-root with-actionbar">
                <ViewToolbar>
                    <HasRight right="appt:create">
                        <Link to="/surgery/create" className="icon-plus" />
                    </HasRight>
                </ViewToolbar>
                <div className="filter-control-box">
                    <DelayInput type="search" className="fill-space" placeholder="Search..." timeout={500} name="searchText" value={this.state.searchText} onChangeDelayed={this.handleElementChange} />
                    <select value={this.state.searchVisitType} name="searchVisitType" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Visit Types</option>
                        <option value="physical">Physical</option>
                        <option value="telemed">Telemedicine</option>
                    </select>
                    <select value={this.state.searchStatus} name="searchStatus" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="active">Active</option>
                        <option value="complete">Completed</option>
                        <option value="noshow">Soft-canceled</option>
                        <option value="canceled">Canceled</option>
                        <option value="notneeded">Canceled - Not Needed</option>
                        <option value="failed">Failed</option>
                    </select>
                    <select value={this.state.searchLocation} name="searchLocation" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Locations</option>
                        {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.shortName || a.name}</option>)}
                    </select>
                    {/*<select value={this.state.searchPerson} name="searchPerson" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <HasRight right="appt:readall">
                            <option value="">Everyone</option>
                        </HasRight>
                        <option value="caller">You</option>
                    </select>*/}
                </div>
                {table}
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.appointments.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private async search() {
        try {
            this.setState({
                isLoading: true
            });

            this.saveSearchState();

            const parameters: string[] = [
                `limit=${PAGE_SIZE}`,
                `offset=${this.state.searchOffset}`,
                "mode=light"
            ];
            if (this.state.searchText) {
                parameters.push(`q=${this.state.searchText}`);
            }
            if (this.state.searchLocation) {
                parameters.push(`location=${this.state.searchLocation}`);
            }
            // if (this.state.searchPerson) {
            //     parameters.push(`person=${this.state.searchPerson}`);
            // }
            if (this.state.searchStatus) {
                parameters.push(`status=${this.state.searchStatus}`);
            }
            if (this.state.searchVisitType) {
                parameters.push(`visit_type=${this.state.searchVisitType}`);
            }

            const results = await V2.get<any[]>(`surgery/summary?${parameters.join("&")}`);

            this.setState({
                appointments: results,
                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Failed to search"
            });
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,

            [name]: value
        }, () => this.search());
    }

    private saveSearchState() {
        const location = this.props.location;
        location.state = {
            searchLocation: this.state.searchLocation,
            searchOffset: this.state.searchOffset,
            searchPerson: this.state.searchPerson,
            searchStatus: this.state.searchStatus,
            searchText: this.state.searchText,
            searchVisitType: this.state.searchVisitType,
        };

        this.props.history.replace(location);
    }

    private statusToText(status: number) {
        switch (status) {
            case 0: return "Pending";
            case 1: return "Active";
            case 2: return "Canceled";
            case 3: return "Soft-canceled";
            case 4: return "Complete";
            case 5: return "Canceled \u2014 Not needed";
            case 6: return "Failed";
            default: return `Unknown: ${status}`;
        }
    }

    private nextPage() {
        this.setState(s => {
            return {
                searchOffset: s.searchOffset + PAGE_SIZE
            };
        }, () => this.search());
    }

    private previousPage() {
        this.setState(s => {
            return {
                searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
            };
        }, () => this.search());
    }
}

export default withAuth(withTitle("Surgery Appointments", SurgeryListView));
